import useReveal from "../hooks/useReveal";
import {
    DollarSign,
    Eye,
    FileWarning,
    Clock,
    Receipt,
    BarChart3,
    Network,
    Truck,
    Activity,
} from "lucide-react";

/* Bento Grid — asymmetric layout per design guidelines */
const PROBLEMS = [
    {
        id: "freight-cost",
        icon: DollarSign,
        title: "Freight cost uncertainty",
        body: "Unclear pricing, hidden surcharges, no single view of true landed cost across lanes.",
        span: "lg:col-span-2 lg:row-span-2",
        accent: true,
    },
    {
        id: "visibility",
        icon: Eye,
        title: "Poor shipment visibility",
        body: "Fragmented tracking across forwarders and carriers — no live picture of risk.",
        span: "lg:col-span-2",
    },
    {
        id: "invoice",
        icon: Receipt,
        title: "Invoice discrepancies",
        body: "Cost leakage hidden inside hundreds of monthly freight invoices.",
        span: "lg:col-span-2",
    },
    {
        id: "detention",
        icon: Clock,
        title: "Detention & demurrage",
        body: "Avoidable charges from missed milestones and slow exception response.",
        span: "lg:col-span-2",
    },
    {
        id: "benchmark",
        icon: BarChart3,
        title: "No procurement benchmarking",
        body: "Tendering blind to market conditions, peer rates and seasonal pricing.",
        span: "lg:col-span-2",
    },
    {
        id: "carrier",
        icon: Truck,
        title: "Carrier performance",
        body: "Limited transparency into transit reliability, on-time delivery & cost-to-serve.",
        span: "lg:col-span-2",
    },
    {
        id: "comms",
        icon: Network,
        title: "Fragmented communication",
        body: "Suppliers, forwarders and carriers operating in disconnected silos.",
        span: "lg:col-span-2",
    },
    {
        id: "surcharge",
        icon: FileWarning,
        title: "Unexpected local charges",
        body: "Origin/destination surcharges that surprise the finance team every quarter.",
        span: "lg:col-span-2",
    },
    {
        id: "reactive",
        icon: Activity,
        title: "Reactive supply chain",
        body: "Decisions made in firefighting mode — never in front of the problem.",
        span: "lg:col-span-2",
    },
];

export default function Problems() {
    const [ref, visible] = useReveal();
    return (
        <section
            id="problems"
            data-testid="problems-section"
            className="relative py-20 sm:py-28 lg:py-36 border-t border-white/5"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div ref={ref} className={`reveal ${visible ? "is-visible" : ""}`}>
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
                        <div>
                            <span className="section-tag" data-testid="problems-tag">
                                Problems · 02
                            </span>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mt-5 leading-tight max-w-2xl">
                                The logistics gaps costing Australian business time, money &amp; control.
                            </h2>
                        </div>
                        <p className="lg:max-w-sm text-zinc-400 leading-relaxed">
                            We focus on the unglamorous parts of freight — the ones quietly
                            draining margin, surprising finance teams and breaking customer
                            promises.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-6 auto-rows-[200px] gap-3 sm:gap-4">
                        {PROBLEMS.map((p) => (
                            <Cell key={p.id} {...p} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

function Cell({ id, icon: Icon, title, body, span, accent }) {
    return (
        <div
            data-testid={`problem-card-${id}`}
            className={`card-glow rounded-xl p-5 sm:p-6 flex flex-col justify-between col-span-2 ${
                span || ""
            } ${accent ? "relative overflow-hidden" : ""}`}
        >
            {accent && (
                <div
                    aria-hidden
                    className="absolute -top-20 -right-20 w-64 h-64 rounded-full"
                    style={{
                        background:
                            "radial-gradient(circle, rgba(0,240,255,0.25), transparent 65%)",
                        filter: "blur(20px)",
                    }}
                />
            )}
            <div className="flex items-center justify-between relative">
                <div
                    className="w-9 h-9 rounded-md flex items-center justify-center"
                    style={{
                        background: "rgba(0,240,255,0.08)",
                        border: "1px solid rgba(0,240,255,0.25)",
                    }}
                >
                    <Icon size={16} className="text-cyan-300" />
                </div>
                <span className="font-mono-accent text-[10px] text-zinc-500">
                    {id.toUpperCase()}
                </span>
            </div>

            <div className="relative">
                <h3
                    className={`font-display text-white tracking-tight ${
                        accent ? "text-2xl sm:text-3xl" : "text-lg sm:text-xl"
                    }`}
                >
                    {title}
                </h3>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{body}</p>
            </div>
        </div>
    );
}
